import { createContext, useContext, useState, useCallback, useRef } from "react";
import { useAppState } from "./appStateContext";
import { useToast } from "./toastContext";
import apiClient from "../api/apiClient";

const CoaJobsContext = createContext();

const POLL_INTERVAL = 3000;
const POLL_TIMEOUT = 900000;

export function CoaJobsProvider({ children }) {
    const { setCOAProcessing } = useAppState();
    const toast = useToast();
    const [activeJobs, setActiveJobs] = useState([]);
    const refreshCallbacksRef = useRef(new Set());

    const registerRefreshCallback = useCallback((cb) => {
        refreshCallbacksRef.current.add(cb);
        return () => refreshCallbacksRef.current.delete(cb);
    }, []);

    const notifyRefresh = useCallback(() => {
        for (const cb of refreshCallbacksRef.current) {
            try { cb(); } catch {}
        }
    }, []);

    const pollJob = useCallback(async (jobId, pollUrl) => {
        const start = Date.now();
        while (Date.now() - start < POLL_TIMEOUT) {
            try {
                const res = await apiClient.get(pollUrl);
                const { status, error, reviewRequired, reviewCount } = res.data || {};
                if (status === "completed") {
                    return {
                        jobId,
                        status: "completed",
                        reviewRequired: reviewRequired === true,
                        reviewCount: Number(reviewCount) || 0,
                    };
                }
                if (status === "failed") return { jobId, status: "failed", error };
            } catch (err) {
                const failedResponse = err.response?.data;
                if (failedResponse?.status === "failed") {
                    return {
                        jobId,
                        status: "failed",
                        error: failedResponse.error,
                    };
                }
                // Network blip — keep polling
            }
            await new Promise((r) => setTimeout(r, POLL_INTERVAL));
        }
        return { jobId, status: "timeout" };
    }, []);

    const submitJobs = useCallback(async (jobs) => {
        // jobs: [{ jobId, pollUrl, fileName }]
        if (!jobs.length) return;

        const entries = jobs.map((j) => ({ ...j, status: "processing" }));
        setActiveJobs(entries);
        setCOAProcessing(true);

        const results = await Promise.all(
            entries.map((j) => pollJob(j.jobId, j.pollUrl))
        );

        const succeeded = results.filter((r) => r.status === "completed").length;
        const failed = results.filter((r) => r.status !== "completed").length;
        const reviewCount = results.reduce(
            (count, result) => count + (result.status === "completed" ? result.reviewCount : 0),
            0
        );

        setActiveJobs([]);
        setCOAProcessing(false);

        if (succeeded > 0) {
            toast.success(
                `${succeeded} COA${succeeded !== 1 ? "s" : ""} processed successfully.`
            );
            notifyRefresh();
        }
        if (reviewCount > 0) {
            toast.warning(
                `Unable to successfully scan ${reviewCount} product profile${reviewCount !== 1 ? "s" : ""}. ${reviewCount !== 1 ? "The uploads have" : "The upload has"} been saved and flagged for manual review.`
            );
        }
        if (failed > 0) {
            toast.error(
                `${failed} COA${failed !== 1 ? "s" : ""} failed to process. Check your email for details.`
            );
            if (succeeded === 0) notifyRefresh();
        }
    }, [setCOAProcessing, toast, pollJob, notifyRefresh]);

    const value = {
        activeJobs,
        submitJobs,
        registerRefreshCallback,
    };

    return (
        <CoaJobsContext.Provider value={value}>
            {children}
        </CoaJobsContext.Provider>
    );
}

export function useCoaJobs() {
    const ctx = useContext(CoaJobsContext);
    if (!ctx) throw new Error("useCoaJobs must be used within CoaJobsProvider");
    return ctx;
}
